/**
 * useGenerationFormHandlers
 *
 * File upload handlers (select, drop) + form submit for GenerationFormV2.
 * Max 50 lines.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface Options {
  setSelectedFile: (f: File | null) => void;
  setPreview: (p: string | null) => void;
  setIsDragOver: (v: boolean) => void;
}

const VALID_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_SIZE = 16 * 1024 * 1024;

export function useGenerationFileHandlers({ setSelectedFile, setPreview, setIsDragOver }: Options) {
  const { t } = useTranslation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!VALID_TYPES.includes(file.type)) { toast.error(t('generation.onlyPngJpgWebp')); return; }
    if (file.size > MAX_SIZE) { toast.error(t('generation.maxFileSize')); return; }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!VALID_TYPES.includes(file.type)) { toast.error(t('generation.onlyPngJpgWebp')); return; }
    if (file.size > MAX_SIZE) { toast.error(t('generation.maxFileSize')); return; }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  return { handleFileSelect, handleDrop };
}

export function useIsDragOver() {
  const [isDragOver, setIsDragOver] = useState(false);
  return { isDragOver, setIsDragOver };
}
